import { lazy, Suspense, useRef, useState } from 'react'
import { gsap, SplitText, useGSAP, scrollToTarget } from '../lib/gsap'
import { useLang } from '../lib/useLang'
import { LINKS, UI } from '../data/content'
import LiquidGlass from '../components/LiquidGlass'
import Magnetic from '../components/Magnetic'
import Logo from '../components/Logo'
import LocalTime from '../components/LocalTime'
import { hasWebGL } from '../lib/webgl'

const Sparkles = lazy(() => import('../components/Sparkles'))

function TalkOrb() {
  const { t } = useLang()
  const ringText = `${t(UI.talk)} • ${t(UI.talk)} • ${t(UI.talk)} • `
  return (
    <Magnetic strength={0.45} inner={0.25}>
      <a href={`mailto:${LINKS.email}`} className="group relative grid h-48 w-48 place-items-center sm:h-60 sm:w-60" aria-label={t(UI.talk)}>
        <LiquidGlass radius={999} bezel={40} refraction={90} blur={3} className="absolute inset-0 transition-transform duration-700 group-hover:scale-105">
          <span />
        </LiquidGlass>
        <span className="absolute inset-4 rounded-full bg-brand opacity-0 shadow-[0_0_80px_rgba(45,92,255,0.9)] transition-opacity duration-500 group-hover:opacity-100" />
        <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full animate-[spin_18s_linear_infinite]" aria-hidden>
          <defs>
            <path id="orbPath" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" />
          </defs>
          <text fill="white" fontSize="12.5" letterSpacing="4" className="label" style={{ fontFamily: 'var(--font-mono)' }}>
            <textPath href="#orbPath">{ringText.toUpperCase()}</textPath>
          </text>
        </svg>
        <span data-magnetic-inner className="relative">
          <Logo variant="white" className="h-14 w-14 transition-transform duration-700 group-hover:rotate-[360deg]" />
        </span>
      </a>
    </Magnetic>
  )
}

function LinkRow({ href, label, sub, onClick, download }) {
  return (
    <a
      href={href}
      onClick={onClick}
      download={download}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel="noreferrer"
      className="group relative flex items-center justify-between overflow-hidden border-b border-white/10 py-6 pr-1"
    >
      <span className="absolute inset-0 origin-bottom scale-y-0 bg-white/[0.04] transition-transform duration-500 ease-[cubic-bezier(.76,0,.24,1)] group-hover:scale-y-100" />
      <span className="relative font-display text-2xl font-medium tracking-[-0.02em] text-white transition-transform duration-500 group-hover:translate-x-4 sm:text-4xl">{label}</span>
      <span className="relative flex items-center gap-4">
        <span className="label hidden text-mist sm:block">{sub}</span>
        <span className="grid h-10 w-10 place-items-center rounded-full border border-white/20 text-white transition-all duration-500 group-hover:rotate-45 group-hover:border-white group-hover:bg-white group-hover:text-ink">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <path d="M7 17 17 7M8 7h9v9" />
          </svg>
        </span>
      </span>
    </a>
  )
}

export default function Contact() {
  const root = useRef(null)
  const { t, lang } = useLang()
  const [copied, setCopied] = useState(false)

  useGSAP(
    () => {
      const big = SplitText.create('[data-big]', { type: 'words,chars', mask: 'words' })
      gsap.from(big.words, { yPercent: 110, stagger: 0.06, duration: 1.4, scrollTrigger: { trigger: '[data-big]', start: 'top 85%' } })

      // Hover wave across the characters
      const chars = big.chars
      const onMove = (e) => {
        chars.forEach((c) => {
          const r = c.getBoundingClientRect()
          const d = Math.hypot(e.clientX - (r.left + r.width / 2), e.clientY - (r.top + r.height / 2))
          const f = Math.max(0, 1 - d / 220)
          gsap.to(c, { y: -f * 18, color: f > 0.15 ? '#7D9BFF' : '#ffffff', duration: 0.5, ease: 'power3.out', overwrite: 'auto' })
        })
      }
      const onLeave = () => gsap.to(chars, { y: 0, color: '#ffffff', duration: 0.8, ease: 'elastic.out(1, 0.4)' })
      const el = root.current.querySelector('[data-big]')
      el.addEventListener('pointermove', onMove)
      el.addEventListener('pointerleave', onLeave)

      gsap.from('[data-row]', { y: 40, opacity: 0, stagger: 0.08, scrollTrigger: { trigger: '[data-rows]', start: 'top 85%' } })
      return () => {
        el.removeEventListener('pointermove', onMove)
        el.removeEventListener('pointerleave', onLeave)
        big.revert()
      }
    },
    { scope: root, dependencies: [lang], revertOnUpdate: true },
  )

  const copy = async (e) => {
    e.preventDefault()
    try {
      await navigator.clipboard.writeText(LINKS.email)
    } catch {
      /* clipboard blocked — the mailto fallback is still visible */
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  return (
    <footer id="contact" ref={root} className="relative overflow-hidden pb-10 pt-16 sm:pb-12 sm:pt-24">
      {hasWebGL && (
        <Suspense fallback={null}>
          <Sparkles className="absolute inset-0" />
        </Suspense>
      )}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[80%]" style={{ background: 'radial-gradient(70% 60% at 50% 100%, rgba(45,92,255,0.28), rgba(45,49,144,0.12) 40%, transparent 75%)' }} />
      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-10">
        <span className="label text-mist">(06) — {t(UI.contactTitle)}</span>
        <div className="mt-8 grid items-center gap-12 lg:grid-cols-[1fr_auto]">
          <h2 key={lang} data-big className="font-display text-[clamp(3rem,8.5vw,8.5rem)] font-semibold leading-[0.9] tracking-[-0.045em] text-white">
            {t(UI.contactBig)}
          </h2>
          <div className="flex justify-center lg:justify-end">
            <TalkOrb />
          </div>
        </div>

        <div className="mt-20 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <a href={`mailto:${LINKS.email}`} className="font-display text-[clamp(1.5rem,4vw,3rem)] font-medium tracking-[-0.03em] text-gradient">
            {LINKS.email}
          </a>
          <LiquidGlass as="button" onClick={copy} radius={999} bezel={14} refraction={28} className="flex h-11 items-center gap-2 px-5 text-sm text-white">
            <span className="relative h-4 w-4">
              <svg className={`absolute inset-0 transition-all duration-300 ${copied ? 'scale-0 opacity-0' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <rect x="9" y="9" width="11" height="11" rx="2" />
                <path d="M5 15V6a2 2 0 0 1 2-2h8" />
              </svg>
              <svg className={`absolute inset-0 text-emerald-400 transition-all duration-300 ${copied ? '' : 'scale-0 opacity-0'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
                <path d="m5 12 5 5 9-10" />
              </svg>
            </span>
            {copied ? t(UI.copied) : t(UI.copyEmail)}
          </LiquidGlass>
        </div>

        <div data-rows className="mt-14 border-t border-white/10">
          <div data-row>
            <LinkRow href={LINKS.github} label="GitHub" sub="/Dudu-Brancalhao" />
          </div>
          <div data-row>
            <LinkRow href={LINKS.linkedin} label="LinkedIn" sub="eduardo-brancalhao" />
          </div>
          <div data-row>
            <LinkRow href={LINKS.resume[lang]} download label={t(UI.resume)} sub="PDF" />
          </div>
        </div>

        <div className="mt-20 flex flex-col gap-6 text-sm text-white/45 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-6 w-6" />
            <span>
              {t(UI.madeBy)} <span className="font-brand text-base text-royal">Dudu</span> · © {new Date().getFullYear()}
            </span>
          </div>
          <div className="flex items-center gap-6">
            <span className="label">
              {t(UI.localTime)} · <LocalTime />
            </span>
            <button onClick={() => scrollToTarget(0, 2)} className="group flex items-center gap-2 text-white/70 hover:text-white">
              {t(UI.backTop)}
              <span className="grid h-9 w-9 place-items-center rounded-full border border-white/20 text-white transition-transform duration-500 group-hover:-translate-y-1">↑</span>
            </button>
          </div>
        </div>
      </div>

    </footer>
  )
}
